import { getClient } from 'lane-shared/apollo';
import { AddProductToChargeDetails } from 'lane-shared/domains/billingPayments/types';
import {
  GroupType,
  CreateChargeRequest,
  ExternalEntityType,
  ExternalActorType,
  ProductItemInput,
  SellingPriceInput,
} from 'graphql-query-contracts';
import { convertToUUID } from 'uuid-encoding';
import {
  currencyToGraphQL,
  convertExternalPayerType,
} from 'domains-billing-web-mappers';

import { ChargeDetails } from '../CreateChargeForm';
import { createChargeMutationQuery } from './chargesInvoiceQueries';

export interface CreateNewChargeDetails extends ChargeDetails {
  channelId: string;
  chargeCreatorUserId: string;
}

export async function createChargeMutation(
  chargeDetails: CreateNewChargeDetails,
  products: AddProductToChargeDetails[] = [],
  isAdminFeeFeatureEnabled: boolean = false
) {
  const {
    invoicee: { _id, type: invoiceeType },
    chargeName,
    chargeDescription,
    currency,
    channelId,
    chargeCreatorUserId,
    invoiceType,
    invoiceDueDate,
    amount,
  } = chargeDetails;

  const productItems = convertProductsToQueryFormat(
    products,
    channelId,
    isAdminFeeFeatureEnabled
  );

  await getClient().mutate<any, { createChargeRequest: CreateChargeRequest }>({
    mutation: createChargeMutationQuery,
    variables: {
      createChargeRequest: {
        name: chargeName,
        description: chargeDescription,
        amount,
        currency: currencyToGraphQL(currency),
        externalPayerId: convertToUUID(_id),
        externalPayerType: convertExternalPayerType(invoiceeType),
        groupId: convertToUUID(channelId),
        groupType: GroupType.GroupTypeActivateChannel,
        externalEntityId: '',
        externalEntityType: ExternalEntityType.ExternalEntityTypeUnknown,
        externalActorId: convertToUUID(chargeCreatorUserId),
        externalActorType: ExternalActorType.ExternalActorTypeActivateUser,
        metadata: '',
        invoiceType,
        invoiceDueDate:
          invoiceDueDate && new Date(invoiceDueDate.toUTCString()),
        items: productItems,
      },
    },
  });
}

export function convertProductsToQueryFormat(
  products: AddProductToChargeDetails[],
  channelId: string,
  isAdminFeeFeatureEnabled: boolean = false
): ProductItemInput[] {
  return products.map(product => {
    const productId = product.productId ? convertToUUID(product.productId) : '';
    const productGroupId = product.productGroupId
      ? convertToUUID(product.productGroupId)
      : convertToUUID(channelId);

    const sellingPrice: SellingPriceInput | undefined =
      product.sellingPrice &&
      product.sellingPrice.grossMarkup !== undefined &&
      product.sellingPrice.grossTax !== undefined &&
      product.sellingPrice.listPrice !== undefined &&
      product.sellingPrice.netPrice !== undefined
        ? {
            grossMarkup: product.sellingPrice.grossMarkup,
            grossTax: product.sellingPrice.grossTax,
            listPrice: product.sellingPrice.listPrice,
            netPrice: product.sellingPrice.netPrice,
            grossAdminFee: isAdminFeeFeatureEnabled
              ? (product.sellingPrice.grossAdminFee ?? 0)
              : 0,
            grossAdminFeeTax: isAdminFeeFeatureEnabled
              ? (product.sellingPrice.grossAdminFeeTax ?? 0)
              : 0,
          }
        : undefined;

    const adminFee =
      isAdminFeeFeatureEnabled && product.adminFee
        ? {
            value: Number(product.adminFee.value),
            adminFeeAmountType: product.adminFee.type,
          }
        : undefined;

    const adminFeeTax =
      isAdminFeeFeatureEnabled && product.adminFeeTax
        ? {
            rate: Number(product.adminFeeTax.value),
            taxAmountType: product.adminFeeTax.type,
          }
        : undefined;

    return {
      product: {
        id: productId,
        name: product.name,
        description: product.description,
        amount: Number(product.rate),
        groupId: productGroupId,
        groupType: GroupType.GroupTypeActivateChannel,
        markup: {
          value: Number(product.markup.value),
          markupAmountType: product.markup.type,
        },
        tax: {
          value: Number(product.tax.value),
          taxAmountType: product.tax.type,
        },
        adminFee,
        adminFeeTax,
        originalPrice: product.originalPrice
          ? {
              amount: Number(product.originalPrice.rate),
              markup: {
                value: Number(product.originalPrice.markup.value),
                markupAmountType: product.originalPrice.markup.type,
              },
              tax: {
                value: Number(product.originalPrice.tax.value),
                taxAmountType: product.originalPrice.tax.type,
              },
              adminFee:
                isAdminFeeFeatureEnabled && product.originalPrice.adminFee
                  ? {
                      value: Number(product.originalPrice.adminFee.value),
                      adminFeeAmountType: product.originalPrice.adminFee.type,
                    }
                  : undefined,
              adminFeeTax:
                isAdminFeeFeatureEnabled && product.originalPrice.adminFeeTax
                  ? {
                      rate: Number(product.originalPrice.adminFeeTax.value),
                      taxAmountType: product.originalPrice.adminFeeTax.type,
                    }
                  : undefined,
            }
          : undefined,
        sellingPrice,
      },
      quantity: Number(product.quantity),
      productCategoryId: product.productCategoryId,
      chargeCodeId: product.chargeCodeId,
    };
  });
}
